<div class="bulk-upload-preview">
  <!-- Validation Failed Banner (wrong template / category mismatch) -->
  <div *ngIf="validationFailed"
    class="alert alert-danger d-flex align-items-start gap-3 mb-3 rounded-3 border-0 shadow-sm p-4">
    <i class="bi bi-x-octagon-fill fs-3 text-danger mt-1"></i>
    <div>
      <h5 class="fw-bold mb-1">Validation Failed</h5>
      <p class="mb-0">{{ validationFailedMessage }}</p>
    </div>
  </div>

  <!-- Success Banner -->
  <div *ngIf="!validationFailed && summary.errorRows === 0 && !commitFailed"
    class="alert alert-success d-flex align-items-center gap-2 mb-3 rounded-3 border-0 shadow-sm">
    <i class="bi bi-check-circle-fill"></i>
    <span class="fw-semibold">Preview ready</span>
    <span class="text-muted ms-2">— Review and edit your data before committing.</span>
  </div>

  <!-- Error Banner (shown after commit fails with validation errors) -->
  <div *ngIf="!validationFailed && summary.errorRows > 0 && !commitFailed"
    class="alert alert-danger d-flex align-items-center gap-2 mb-3 rounded-3 border-0 shadow-sm">
    <i class="bi bi-exclamation-triangle-fill"></i>
    <span class="fw-semibold">{{ summary.errorRows }} row(s) have errors</span>
    <span class="text-muted ms-2">— Hover on the Status badge to see the error details.</span>
  </div>

  <!-- Commit Failed Banner — allows proceeding anyway -->
  <div *ngIf="commitFailed"
    class="alert alert-warning d-flex align-items-center gap-2 mb-3 rounded-3 border-0 shadow-sm">
    <i class="bi bi-exclamation-triangle-fill"></i>
    <span class="fw-semibold">Commit encountered {{ summary.errorRows }} error(s)</span>
    <span class="text-muted ms-2">— You can fix the errors or proceed anyway (error rows will be skipped).</span>
  </div>

  <!-- Job Info: Category + File -->
  <div class="bg-white rounded-3 border shadow-sm p-3 mb-3">
    <div class="d-flex align-items-center gap-4 flex-wrap">
      <div class="d-flex align-items-center gap-2" *ngIf="categoryName">
        <span class="badge rounded-pill bg-secondary-subtle text-secondary px-3 py-2" style="font-size: 0.75rem;">
          <i class="bi bi-tag me-1"></i>{{ categoryName }}
        </span>
      </div>
      <div class="d-flex align-items-center gap-2" *ngIf="fileName">
        <span class="badge rounded-pill bg-light text-dark border px-3 py-2" style="font-size: 0.75rem;">
          <i class="bi bi-file-earmark-text me-1"></i>{{ fileName }}
        </span>
      </div>
      <div class="d-flex align-items-center gap-2" *ngIf="jobId">
        <span class="badge rounded-pill bg-light text-muted border px-3 py-2" style="font-size: 0.7rem;">
          Job #{{ jobId | slice:0:8 }}
        </span>
      </div>
    </div>
  </div>

  <!-- Summary Stats + Filter Pills -->
  <div class="bg-white rounded-3 border shadow-sm p-4 mb-3">
    <div class="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-3">
      <!-- Stats -->
      <div class="d-flex gap-4 align-items-center flex-wrap">
        <div class="stat-item">
          <span class="text-muted small">Total:</span>
          <strong class="ms-1">{{ summary.totalRows }}</strong>
        </div>
        <div class="stat-item">
          <span class="text-muted small">Valid:</span>
          <strong class="ms-1 text-success">{{ summary.validRows }}</strong>
        </div>
        <div class="stat-item">
          <span class="text-muted small">Errors:</span>
          <strong class="ms-1 text-danger">{{ summary.errorRows }}</strong>
        </div>
        <div class="stat-item" *ngIf="summary.warningRows > 0">
          <span class="text-muted small">Warnings:</span>
          <strong class="ms-1 text-warning">{{ summary.warningRows }}</strong>
        </div>
        <div class="stat-item" *ngIf="editedRowCount > 0">
          <span class="text-muted small">Edited:</span>
          <strong class="ms-1" style="color: #FFA000;">{{ editedRowCount }}</strong>
        </div>
      </div>

      <!-- Filter Pills -->
      <div class="d-flex gap-2 flex-wrap">
        <button class="filter-pill" [class.active]="activeFilter === 'all'" (click)="setFilter('all')">
          All
        </button>
        <button class="filter-pill" [class.active]="activeFilter === 'valid'" (click)="setFilter('valid')">
          Valid
        </button>
        <button class="filter-pill" [class.active]="activeFilter === 'errors'" (click)="setFilter('errors')">
          <i class="bi bi-exclamation-circle me-1"></i>Errors
        </button>
        <button class="filter-pill" *ngIf="summary.warningRows > 0" [class.active]="activeFilter === 'warnings'"
          (click)="setFilter('warnings')">
          Warnings
        </button>
      </div>
    </div>


  </div>

  <!-- AG Grid -->
  <div class="ag-grid-wrapper bg-white rounded-3 border shadow-sm">
    <ag-grid-angular class="ag-theme-alpine" style="width: 100%; height: 500px;" [columnDefs]="columnDefs"
      [rowData]="rowData" [defaultColDef]="defaultColDef" [getRowId]="getRowId" [getRowStyle]="getRowStyle"
      [tooltipShowDelay]="300" [headerHeight]="45" [rowHeight]="42" [undoRedoCellEditing]="true"
      [undoRedoCellEditingLimit]="20" [suppressColumnVirtualisation]="true" [pagination]="true"
      [paginationPageSize]="pageSize" [paginationPageSizeSelector]="pageSizeOptions"
      [suppressPaginationPanel]="isPaginated" [suppressFieldDotNotation]="true"
      overlayLoadingTemplate="<div class='d-flex flex-column align-items-center gap-2 py-5'><div class='spinner-border text-secondary' role='status'></div><span class='text-muted small'>Loading job data...</span></div>"
      (gridReady)="onGridReady($event)" (cellValueChanged)="onCellValueChanged($event)"
      (paginationChanged)="onPaginationChanged($event)">
    </ag-grid-angular>

    <!-- Server-side pagination bar (shown only when backend paginates the data) -->
    <div class="ag-paging-panel ag-unselectable" *ngIf="isPaginated">
      <span class="ag-paging-row-summary-panel">
        <span class="ag-paging-row-summary-panel-number">{{ pageStart }}</span>
        to
        <span class="ag-paging-row-summary-panel-number">{{ pageEnd }}</span>
        of
        <span class="ag-paging-row-summary-panel-number">{{ totalRows }}</span>
      </span>

      <span class="ag-paging-page-summary-panel">
        <div class="ag-paging-page-size">
          <span class="ag-paging-page-size-label">Page Size:</span>
          <select class="ag-paging-page-size-select" [disabled]="isLoading" (change)="onPageSizeChange($event)">
            <option *ngFor="let size of pageSizeOptions" [value]="size" [selected]="size === pageSize">{{ size }}
            </option>
          </select>
        </div>

        <button class="ag-paging-button" [disabled]="page <= 1 || isLoading" (click)="goToPage(1)">
          <span class="ag-icon ag-icon-first"></span>
        </button>
        <button class="ag-paging-button" [disabled]="page <= 1 || isLoading" (click)="prevPage()">
          <span class="ag-icon ag-icon-previous"></span>
        </button>
        <span class="ag-paging-description">
          Page
          <span class="ag-paging-number">{{ page }}</span>
          of
          <span class="ag-paging-number">{{ totalPages }}</span>
        </span>
        <button class="ag-paging-button" [disabled]="page >= totalPages || isLoading" (click)="nextPage()">
          <span class="ag-icon ag-icon-next"></span>
        </button>
        <button class="ag-paging-button" [disabled]="page >= totalPages || isLoading" (click)="goToPage(totalPages)">
          <span class="ag-icon ag-icon-last"></span>
        </button>
      </span>
    </div>
  </div>

  <!-- Note: edits are preserved across pages when paginated -->
  <div class="text-muted small mt-2" *ngIf="isPaginated && editedRowCount > 0">
    <i class="bi bi-info-circle me-1"></i>
    Edits are kept as you navigate between pages and will all be included when you commit.
  </div>

  <!-- Footer Actions -->
  <div class="d-flex gap-3 mt-4">
    <!-- Primary: Commit — always available; error rows are skipped server-side -->
    <button class="btn btn-secondary text-white px-4 py-2 fw-semibold" *ngIf="!commitFailed" [disabled]="isSubmitting"
      (click)="commitChanges()">
      <span *ngIf="!isSubmitting">
        <i class="bi bi-check2-all me-1"></i>
        <ng-container *ngIf="summary.errorRows > 0; else plainCommitLabel">Commit valid rows (skip
          errors)</ng-container>
        <ng-template #plainCommitLabel>Commit</ng-template>
      </span>
      <span *ngIf="isSubmitting">
        <i class="bi bi-arrow-repeat spin me-1"></i>Committing...
      </span>
    </button>

    <!-- After commit failure: allow proceeding anyway -->
    <button class="btn btn-warning text-dark px-4 py-2 fw-semibold" *ngIf="commitFailed" [disabled]="isSubmitting"
      (click)="commitWithErrors()">
      <span *ngIf="!isSubmitting">
        <i class="bi bi-exclamation-triangle me-1"></i>Commit anyway (skip errors)
      </span>
      <span *ngIf="isSubmitting">
        <i class="bi bi-arrow-repeat spin me-1"></i>Committing...
      </span>
    </button>

    <!-- Reset edits — only shown once there are unsaved inline edits -->
    <button class="btn btn-outline-secondary px-4 py-2 fw-semibold" *ngIf="editedRowCount > 0" [disabled]="isSubmitting"
      (click)="resetEdits()">
      <i class="bi bi-arrow-counterclockwise me-1"></i>Reset edits ({{ editedRowCount }})
    </button>

    <button class="btn btn-outline-secondary px-4 py-2 fw-semibold" (click)="goBack()">
      <i class="bi bi-arrow-left me-1"></i>Re-upload with different file
    </button>
  </div>

  <!-- Hint for errors -->
  <div class="text-muted small mt-3" *ngIf="summary.errorRows > 0 && !commitFailed && !validationFailed">
    <i class="bi bi-info-circle me-1"></i>
    Click on a highlighted cell to edit it directly (errors clear automatically when you correct the value), or commit
    now to skip the error rows and save only the valid ones.
  </div>
  <div class="text-muted small mt-3" *ngIf="commitFailed">
    <i class="bi bi-info-circle me-1"></i>
    You can fix the errors above, or click "Commit anyway" to skip error rows and commit only valid data.
  </div>
</div>